// Augusta University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Augusta publishes no Common Data Set (its Institutional Effectiveness
//    reporting calendar lists the survey, but no file is posted). The
//    first-year counts come from the U.S. Department of Education's IPEDS
//    Admissions survey for Fall 2024, first-time degree-seeking students
//    (College Navigator and the ADM2024 complete data file, unit 482149).
//    Quirk: Augusta's own AU Facts Admissions table (22,411 applied and
//    11,349 accepted for Fall 2025) counts every level of applicant,
//    graduate and professional included, so it is not a first-year rate and
//    is not used here.
//  - Augusta Admissions pages: Freshman Admissions (requirements, test
//    scores and the "Important Dates for 2027 Applications" table, Fall 2027
//    entry) and Apply to Augusta University (separate applications).
//  - Augusta Financial Aid, Cost of Attendance, Academic Year 2026-2027
//    (undergraduate on campus, in-state and out-of-state).
//  - College Scorecard, school id 482149 (net price by family income), read
//    from the Scorecard's school page because the API's shared demo key was
//    over its rate limit.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    ipeds: {
        label: 'IPEDS Admissions, Fall 2024, U.S. Department of Education',
        url: 'https://nces.ed.gov/collegenavigator/?id=482149#admsns',
        read: '2026-10-01',
    },
    facts: {
        label: 'Augusta University Institutional Effectiveness, AU Facts: Admissions',
        url: 'https://www.augusta.edu/ie/facts/admissions.php',
        read: '2026-10-01',
    },
    freshman: {
        label: 'Augusta University Admissions, Freshman Admissions',
        url: 'https://www.augusta.edu/admissions/freshman.php',
        read: '2026-10-01',
    },
    apply: {
        label: 'Augusta University Admissions, Apply to Augusta University',
        url: 'https://www.augusta.edu/admissions/apply.php',
        read: '2026-10-01',
    },
    costs: {
        label: 'Augusta University Financial Aid, Cost of Attendance',
        url: 'https://www.augusta.edu/finaid/costofattendance.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?482149-Augusta-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'augusta-university',
    kbId: 'augusta_university',
    name: 'Augusta University',
    shortName: 'Augusta',
    aliases: ['Augusta U', 'GRU'],
    location: 'Augusta, GA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Augusta brand colors page); decoration only.
    branding: {
        primary: '#002F55',
        secondary: '#A5ACAF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/augusta_university_logo.png',
    },

    admission: {
        entering: 'Fall 2024',
        cdsCycle: null,
        applied: 6011,
        admitted: 5194,
        enrolled: 1303,
        source: 'ipeds',
        where: 'first-time, degree-seeking undergraduates',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Augusta publishes no first-year acceptance rate by college or major. Its AU Facts admissions table counts every applicant, graduate and professional programs included, so it is not a first-year rate.',
        source: 'facts',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Augusta as a freshman. Some health programs take their own application:',
        rows: [
            { school: 'Capacity-limited allied health programs', requirement: 'A separate application for Clinical Laboratory Science, Dental Hygiene, Health Information Administration, Nuclear Medicine Technology, Radiation Therapy and Respiratory Therapy.' },
            { school: 'Nursing', requirement: 'A separate application through NursingCAS.' },
        ],
        source: 'apply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Final application deadline', value: 'July 1' },
        rows: [
            { plan: 'Early Action', deadline: 'October 31', decision: 'On or before November 15', binding: false },
            { plan: 'Final application deadline', deadline: 'July 1', decision: null, binding: false },
        ],
        note: 'The AU scholarship deadline is February 1, 2027. Freshman housing applications open December 1, 2026, and National Decision Day is May 1, 2027. The application fee is $60. Augusta does not publish a decision date for the final deadline.',
        source: 'freshman',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Augusta requires official SAT, ACT or CLT scores from every freshman applicant; only one is needed. The minimums are 480 in SAT reading and writing and 440 in SAT math, or 17 in ACT English or reading and 17 in ACT math, or 36 in CLT verbal reasoning and grammar/writing and 13 in CLT quantitative reasoning. Meeting them does not guarantee admission. Augusta superscores, using the highest scores you send.',
        source: 'freshman',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'freshman',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7134, 24568] },
            { item: 'Fees', amounts: [1350, 1350] },
            { item: 'Housing (on campus)', amounts: [8564, 8564] },
            { item: 'Food', amounts: [4574, 4574] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1330, 1330] },
            { item: 'Transportation', amounts: [1828, 1828] },
            { item: 'Miscellaneous', amounts: [3078, 3078] },
            { item: 'Student loan fee', amounts: [96, 96] },
        ],
        totals: [27954, 45388],
        note: 'For undergraduates living on campus. Augusta calls these estimates and says to check POUNCE for your own estimated costs.',
        source: 'costs',
        netPrice: {
            average: 12017,
            byIncome: [
                { income: '$0 to $30,000', amount: 9875 },
                { income: '$30,001 to $48,000', amount: 9940 },
                { income: '$48,001 to $75,000', amount: 11237 },
                { income: '$75,001 to $110,000', amount: 14462 },
                { income: 'Over $110,000', amount: 16731 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Augusta\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
